import { AnimatePresence, motion } from 'motion/react'
import { isTouchDevice } from '@/arcade'
import { getLevel } from '@/game/levels'

interface TutorialOverlayProps {
  levelIndex: number
  visible: boolean
  onDismiss: () => void
}

const MECHANIC_HINTS: { tile: string; text: string }[] = [
  { tile: 's', text: 'Ice tiles have no friction — Ember keeps sliding straight through them.' },
  { tile: 'b', text: 'Breakable blocks shatter and stop you once, then they are gone. Use them to park.' },
  { tile: 'k', text: 'Grab the key mid-slide, then slide through its matching door.' },
  { tile: 't', text: 'Teleport pads fling Ember to their pair, and the slide continues from there.' },
  { tile: 'w', text: 'Rolling over a switch opens every door on the board at once.' },
  { tile: 'l', text: 'Lava and water are lethal. Never let a slide end inside them.' },
]

function firstMechanicHint(levelIndex: number): string | null {
  const level = getLevel(levelIndex)
  if (!level) return null
  const prev = levelIndex > 0 ? getLevel(levelIndex - 1) : undefined
  const flat = level.grid.join('')
  const prevFlat = prev ? prev.grid.join('') : ''
  for (const m of MECHANIC_HINTS) {
    if (flat.includes(m.tile) && !prevFlat.includes(m.tile)) return m.text
  }
  return null
}

export function TutorialOverlay({ levelIndex, visible, onDismiss }: TutorialOverlayProps) {
  let hint: string | null
  if (levelIndex === 0) {
    hint = isTouchDevice()
      ? 'Swipe anywhere or use the joystick. Ember slides until it hits a wall.'
      : 'Press an arrow key or WASD. Ember slides until it hits a wall.'
  } else if (levelIndex === 1) {
    hint = 'Slide over coins to collect them — routes with fewer slides earn more stars.'
  } else {
    hint = firstMechanicHint(levelIndex)
  }

  return (
    <AnimatePresence>
      {visible && hint && (
        <div className="pointer-events-none absolute inset-x-0 bottom-[27%] z-20 flex justify-end px-3 sm:bottom-4 sm:px-4">
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            onClick={onDismiss}
            className="pointer-events-auto max-w-[17rem] rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-raised border border-[var(--ember)]/40 px-3.5 py-2.5 text-left text-xs leading-snug text-[var(--text)] sm:max-w-xs sm:text-sm"
          >
            {hint}
            <span className="block mt-1 text-[11px] text-[var(--text-faint)] sm:text-xs">Tap to dismiss</span>
          </motion.button>
        </div>
      )}
    </AnimatePresence>
  )
}
